<template>
  <fragment>
    <div class="col-12">
      <div class="dashboard-tabs">
        <button
          v-for="creditType in creditTypes"
          :key="creditType.id"
          type="button"
          :class="selectedCreditType === creditType.id && 'active'"
          :disabled="selectedCreditType === creditType.id || credits.loading"
          @click="selectCreditType(creditType.id)"
        >
          {{ creditType.title }}
        </button>
      </div>
    </div>
    <div class="col-12">
      <div class="dash-section">
        <div class="header">
          <div>
            <h2 id="sectionTitle" class="title d-inline">
              Credits
              <span
                class="icon alt clickable-question"
                @click="openCreditDocumentationModal"
              >
                <font-awesome-icon :icon="['fas', 'question-circle']" />
              </span>
            </h2>
          </div>
          <div class="controls section-header-offset">
            <custom-tags-input :value="credits.customCampusTags" @input="setCustomTags" />
            <header-buttons
              class="dash-button-group"
              :from="from"
              :through="through"
              @change="handleDateChange"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 col-lg-7">
      <div class="dash-section contains-list">
        <metrics :credit-type="selectedCreditType" />
        <div class="dash-sub-section">
          <list-skeleton
            v-if="credits.ledger.loading && !credits.ledger.hasNext"
            list-type="session"
          />
          <ul v-else class="dashboard-list color-coded">
            <li v-if="!credits.ledger.entries.length" class="empty">No Results</li>
            <li
              v-for="entry in credits.ledger.entries"
              :key="entry.when"
              class="row"
              :class="getEntryClass(entry)">
              <div class="col-9 subject">
                <div>
                  <div class="primary">{{ getEntryDate(entry) }}</div>
                  <div class="secondary">{{ getExplaination(entry) }}</div>
                </div>
              </div>
              <div class="col-3 data right-align">
                <div v-if="entry.explainerTags.some(x => x === 'CounselorNoShow')" class="status Pending">
                  PENDING REFUND
                </div>
                <div>
                  <span class="credits">
                    <template v-if="entry.amount >= 0">+</template>{{ entry.amount }}<img src="@/assets/icon/icon-credit.svg" alt="Credit" />
                  </span>
                </div>
              </div>
            </li>
          </ul>
          <div v-if="!$globals.isMobile && credits.ledger.hasNext" class="list-progress-bar">
            <div class="value"></div>
            <span class="sr-only">loading</span>
          </div>

          <div
            v-if="credits.ledger.entries.length > 0 && !credits.ledger.loading && !credits.ledger.hasNext"
            class="end-of-list"
          >
            <font-awesome-icon :icon="['fas', 'check-circle']" class="icon alt" />
            <p>All Results Loaded</p>
          </div>
          <div v-if="$globals.isMobile && credits.ledger.hasNext && !credits.loading" class="load-more">
            <button class="dash-button" @click="credits.ledger.fetch()">
              Load More Results
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="col-12 col-lg-5">
      <credit-usage :credit-type="selectedCreditType" />
    </div>
  </fragment>
</template>

<script>
import CreditUsage from './components/CreditUsage.vue';
import HeaderButtons from './components/HeaderButtons.vue';
import Metrics from './components/Metrics.vue'
import ListSkeleton from '../../ListSkeleton.vue'
import DocumentationModal from './components/DocumentationModal.vue'
import moment from 'moment';
import CustomTagsInput from '../../CustomTagsInput.vue';

export default {
  name: 'CreditsTab',

  components: {
    CreditUsage,
    HeaderButtons,
    Metrics,
    ListSkeleton,
    DocumentationModal,
    CustomTagsInput,
  },

  inject:['credits'],

  data() {
    return {
      selectedCreditType: 'individual',
      creditTypes: [
        { id:'individual', title: 'Individual' },
        { id:'workshop', title: 'Workshop' },
      ],
    }
  },

  computed:{
    from() {
      return this.credits.from ? this.credits.from.format('YYYY-MM-DD') : null;
    },
    through() {
      return this.credits.through ? this.credits.through.format('YYYY-MM-DD') : null;
    }
  },

  created() {
    window.addEventListener('scroll', this.handleScroll, { once:true });
  },

  destroyed() {
    window.removeEventListener('scroll', this.handleScroll, { once:true });
  },

  methods: {
    async handleScroll() {
      if (!this || !this.credits) return;

      const atBottomOfWindow =
        document.documentElement.scrollTop + window.innerHeight >=
        document.documentElement.offsetHeight * 0.8;
      if (!this.$globals.isMobile
        && atBottomOfWindow
        && this.credits.ledger.hasNext) {
        await this.credits.ledger.fetch();
      }
      window.addEventListener('scroll', this.handleScroll, { once:true });
    },

    selectCreditType(creditType) {
      this.selectedCreditType = creditType
      if (creditType === 'individual') {
        this.credits.ledger.asIndividual()
      }

      if (creditType === 'workshop') {
        this.credits.ledger.asWorkshop()
      }
      this.credits.ledger.fetch();
    },

    handleDateChange({ from, through }) {
      return this.credits
        .between(from, through)
        .fetchAll()
    },
    getEntryDate(entry) {
      return moment(entry.when).format('MM/DD/yyyy [at] h:mma');
    },
    getEntryClass(entry) {
      if (!entry.explainerTags || !entry.explainerTags.length) return 'successful' // green

      if (entry.explainerTags.some(x => x === 'CreditsAdded')) return 'cancelled' // yellow

      if (entry.explainerTags.some(x => x === 'CounselorNoShow')) return 'not-attended' // red
      if (entry.explainerTags.some(x => x === 'StudentNoShow')) return 'not-attended' // red
      if (entry.explainerTags.some(x => x === 'LateCancel')) return 'not-attended' // red

      if (entry.explainerTags.some(x => x === 'SessionUpcoming')) return 'successful' // green

      return 'successful' // green
    },
    getExplaination(entry) {
      if (!entry.explainerTags || !entry.explainerTags.length) return 'Successful session'

      if (entry.explainerTags.some(x => x === 'CreditsAdded')) return 'Credits added'

      if (entry.explainerTags.some(x => x === 'CounselorNoShow') && entry.explainerTags.some(x => x === 'StudentNoShow')) {
        return 'User and Counselor no show.'
      }
      else if (entry.explainerTags.some(x => x === 'CounselorNoShow')) return 'Counselor no show.'
      else if (entry.explainerTags.some(x => x === 'StudentNoShow')) return 'User no show'

      if (entry.explainerTags.some(x => x === 'LateCancel')) return 'Session canceled by user within 24 hours of start time'

      if (entry.explainerTags.some(x => x === 'SessionUpcoming')) return 'Upcoming Session'

      return 'Successful session'
    },
    openCreditDocumentationModal() {
      this.$modal.show(DocumentationModal)
    },
    setCustomTags(tagsList) {
      this.credits.withCustomTags(tagsList);
      this.credits.fetchAll();
    }
  }
};
</script>

<style lang="scss" scoped>

.clickable-question {
  cursor: pointer;
  :hover {
    color: $green;
  }
}

.dash-section {
  min-height: 0;
  .controls {
    justify-content: space-between;

  }
  .header {

    @media screen and (max-width: 992px) {
      flex-direction: column;
      .controls {
        justify-content: space-between;
        flex-direction: column;
        margin-right: 0px;
        margin-top: 0px;

        div {
          margin-right: 0px;
          margin-top: .75rem;
          width: 100%;
        }
        .admin-custom-tags {
          width: 100%;
        }
      }
      .clickable-question {
        float: right;
      }
    }
  }
}

</style>
